import { useCallback, useState, useEffect, useMemo } from "react";
import { useLocation, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import "./ReportDetail.scss";
import { MotifIcon } from "@ey-xd/motif-react";
import { IconoirArrowLeft } from "@ey-xd/motif-icon";
import BreadcrumbHome from "../../components/BreadcrumbHome";
import PowerBIReport from "../../components/PowerBIReport/PowerBIReport";
import { fetchPBIEmbedDetails } from "../../store/slices/CTPBIEmbedDetailsSlice";

const ReportDetail = () => {
  const { id: reportId } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const { title, description } = (location as any).state || {};

  const [embedgingDetails, setEmbedgingDetails] = useState<{
    embedUrl?: string;
    embedToken?: string;
    loading: boolean;
    error?: string | null;
  }>({ loading: true, error: null });

  const fetchEmbedDetails = useCallback(async () => {
    if (!reportId) return;

    setEmbedgingDetails(prev => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const action = await dispatch(fetchPBIEmbedDetails({ reportId }));
      const payload = unwrapResult ? unwrapResult(action) : action.payload;

      setEmbedgingDetails({
        embedUrl: payload.embedUrl,
        embedToken: payload.embedToken,
        loading: false,
        error: null,
      });

    } catch (err: any) {
      setEmbedgingDetails({
        embedUrl: undefined,
        embedToken: undefined,
        loading: false,
        error: err?.message || "Failed to load embed details",
      });
    }
  }, [dispatch, reportId]);

  useEffect(() => {
    fetchEmbedDetails();
  }, [fetchEmbedDetails]);

  const breadcrumbItems = useMemo(
    () => [
      {
        label: "External Submission",
        value: "Home",
        elementKey: "submission",
        text: "Home",
        url: `/`,
        icon: <MotifIcon src={IconoirArrowLeft} />,
      },
      {
        label: "External Submission",
        value: "Reporting",
        elementKey: "submission",
        text: "Reporting",
        url: `/report`,
        icon: <MotifIcon src={IconoirArrowLeft} />,
      },
      {
        label: "External Submission",
        value: "Title",
        elementKey: "submission",
        text: title || "",
        disabled: true,
      },
    ],
    [title]
  );

  const hasEmbedCreds =
    !!embedgingDetails.embedUrl && !!embedgingDetails.embedToken;

  return (
    <div className="report-detail-container-wrapper">
      <section className="report-detail-header-banner">
        <div className="report-detail-header-content">
          <div className="breadcrumb-grid">
            <BreadcrumbHome items={breadcrumbItems} ariaLabel="Breadcrumb navigation" />
          </div>

          {title && description && (
            <>
              <h1 className="report-detail-header-title">{title}</h1>
              <p className="report-detail-title-description">{description}</p>
            </>
          )}
        </div>
      </section>

      <div className="report-detail-section">
        <PowerBIReport
          reportId={reportId!}
          embedUrl={hasEmbedCreds ? embedgingDetails.embedUrl! : ""}
          accessToken={hasEmbedCreds ? embedgingDetails.embedToken! : ""}
          fetchingToken={embedgingDetails.loading}
          error={embedgingDetails.error || null}
          onRefresh={fetchEmbedDetails}
        />
      </div>
    </div>
  );
};

export default ReportDetail;